import { useState } from "react";
import { MapPin, X } from "lucide-react";

/**
 * Carte stylisée (non géographiquement exacte) des territoires Outre-mer français.
 * Chaque territoire est un cercle cliquable scalé selon l'activité (nombre de salariés).
 * Au clic : callback onSelect(territoire) + tooltip détaillé.
 */

// Positions approximatives sur un canvas 800x400 (ordre A→Z pour collision), tailles 0..1
const TERRITOIRES_MAP = [
  { name: "Guadeloupe",               cx: 170, cy: 110, flag: "🇬🇵", color: "#C5A059" },
  { name: "Saint-Martin",             cx: 170, cy: 60,  flag: "🇲🇫", color: "#F59E0B", small: true },
  { name: "Saint-Barthélemy",         cx: 220, cy: 60,  flag: "🇫🇷", color: "#F59E0B", small: true },
  { name: "Martinique",               cx: 170, cy: 180, flag: "🇲🇶", color: "#5B8DEF" },
  { name: "Guyane",                   cx: 170, cy: 270, flag: "🇬🇫", color: "#43A974" },
  { name: "Saint-Pierre-et-Miquelon", cx: 100, cy: 40,  flag: "🇵🇲", color: "#0EA5E9", small: true },
  { name: "La Réunion",               cx: 500, cy: 210, flag: "🇷🇪", color: "#EF4444" },
  { name: "Mayotte",                  cx: 440, cy: 180, flag: "🇾🇹", color: "#7B6FBF", small: true },
  { name: "Nouvelle-Calédonie",       cx: 650, cy: 230, flag: "🇳🇨", color: "#14B8A6" },
  { name: "Polynésie française",      cx: 720, cy: 170, flag: "🇵🇫", color: "#EC4899" },
  { name: "Wallis-et-Futuna",         cx: 620, cy: 150, flag: "🇼🇫", color: "#A78BFA", small: true },
];

export default function CarteOutremer({ territoires = [], onSelect, selected }) {
  const [hover, setHover] = useState(null);

  // Lookup activity by name
  const activity = new Map(territoires.map((t) => [t.territoire, t]));
  const totalSal = Math.max(...territoires.map((t) => t.salaries || 0), 1);

  const getRadius = (name, small) => {
    const t = activity.get(name);
    if (!t) return small ? 7 : 12;
    const base = small ? 8 : 14;
    const scale = Math.sqrt((t.salaries || 0) / totalSal); // sqrt scale pour éviter trop gros
    return base + scale * (small ? 10 : 20);
  };

  return (
    <div className="relative bg-gradient-to-br from-sky-50 to-blue-50 border border-slate-200 rounded-xl overflow-hidden" data-testid="carte-outremer">
      <div className="px-5 py-3 border-b border-slate-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-[#C5A059]" />
          <h3 className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>Carte des territoires Outre-mer</h3>
        </div>
        <span className="text-xs text-slate-500">Cliquez sur un territoire</span>
      </div>

      <div className="relative">
        <svg viewBox="0 0 800 320" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
          {/* Zone Caraïbes label */}
          <text x="170" y="25" textAnchor="middle" className="fill-slate-400" style={{ fontSize: 10, fontFamily: "Outfit, sans-serif", letterSpacing: 2, textTransform: "uppercase" }}>Caraïbes</text>
          <text x="470" y="140" textAnchor="middle" className="fill-slate-400" style={{ fontSize: 10, fontFamily: "Outfit, sans-serif", letterSpacing: 2 }}>Océan Indien</text>
          <text x="680" y="110" textAnchor="middle" className="fill-slate-400" style={{ fontSize: 10, fontFamily: "Outfit, sans-serif", letterSpacing: 2 }}>Pacifique</text>

          {/* Lignes guides douces */}
          <line x1="40" y1="310" x2="290" y2="310" stroke="#CBD5E1" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
          <line x1="400" y1="310" x2="550" y2="310" stroke="#CBD5E1" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
          <line x1="580" y1="310" x2="770" y2="310" stroke="#CBD5E1" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />

          {TERRITOIRES_MAP.map((t) => {
            const act = activity.get(t.name);
            const r = getRadius(t.name, t.small);
            const hasData = act && act.salaries > 0;
            const isSelected = selected === t.name;
            const isHover = hover?.name === t.name;
            return (
              <g
                key={t.name}
                onMouseEnter={() => setHover(t)}
                onMouseLeave={() => setHover(null)}
                onClick={() => onSelect?.(t.name)}
                style={{ cursor: onSelect ? "pointer" : "default" }}
                data-testid={`carte-${t.name.replace(/\s/g, "-")}`}
              >
                {/* Pulse ring si actif */}
                {hasData && (
                  <circle cx={t.cx} cy={t.cy} r={r + 3} fill="none" stroke={t.color} strokeWidth="1.5" opacity={isSelected ? 0.9 : 0.3} className={isSelected ? "" : "animate-pulse"} />
                )}
                <circle
                  cx={t.cx} cy={t.cy} r={r}
                  fill={hasData ? t.color : "#CBD5E1"}
                  fillOpacity={hasData ? 0.85 : 0.5}
                  stroke={isSelected || isHover ? "#1E293B" : t.color}
                  strokeWidth={isSelected ? 3 : isHover ? 2 : 1}
                  style={{ transition: "all 150ms" }}
                />
                <text
                  x={t.cx} y={t.cy + 4}
                  textAnchor="middle"
                  className="pointer-events-none select-none"
                  style={{ fontSize: t.small ? 10 : 14 }}
                >
                  {t.flag}
                </text>
                <text
                  x={t.cx} y={t.cy + r + 14}
                  textAnchor="middle"
                  className="pointer-events-none select-none fill-slate-700 font-medium"
                  style={{ fontSize: 10, fontFamily: "Outfit, sans-serif" }}
                >
                  {t.name}
                </text>
                {act && (
                  <text
                    x={t.cx} y={t.cy + r + 26}
                    textAnchor="middle"
                    className="pointer-events-none select-none"
                    style={{ fontSize: 9, fill: t.color, fontWeight: 600 }}
                  >
                    {act.salaries || 0} sal · {act.missions_en_cours || 0} mis
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Tooltip hover */}
        {hover && activity.get(hover.name) && (
          <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg border border-slate-200 p-3 w-56 pointer-events-none text-xs">
            <div className="flex items-center gap-2 font-semibold text-slate-900">
              <span className="text-lg">{hover.flag}</span>
              <span>{hover.name}</span>
            </div>
            <div className="mt-2 space-y-0.5 text-slate-600">
              <div><strong>{activity.get(hover.name).entreprises}</strong> entreprise(s)</div>
              <div><strong>{activity.get(hover.name).salaries}</strong> salarié(s)</div>
              <div><strong>{activity.get(hover.name).missions_en_cours}</strong> mission(s) en cours</div>
              <div><strong>{activity.get(hover.name).heures_mois.toFixed(1)}h</strong> ce mois</div>
              <div>Dû : <strong className={activity.get(hover.name).montant_du > 0 ? "text-red-600" : "text-slate-600"}>{activity.get(hover.name).montant_du.toLocaleString("fr-FR")} €</strong></div>
            </div>
          </div>
        )}

        {/* Légende */}
        <div className="absolute bottom-3 left-3 bg-white/80 backdrop-blur border border-slate-200 rounded-md px-3 py-1.5 text-[10px] text-slate-600 flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-slate-300"></span> Pas d'activité</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#C5A059]"></span> Actif · taille = nb salariés</span>
        </div>
      </div>
    </div>
  );
}
